import {IdentityProviderGroup, IdentityProviderGroupWrapper} from "@/models/IdentityProviderGroup.ts";
import React, {FC, useState} from "react";
import {useLocale} from "@/contexts/localeContext.tsx";
import {TableContextProvider} from "@/contexts/table/TableContextProvider.tsx";
import {TranslationKeys} from "@/contexts/TranslationKeys.ts";
import TableTab from "@/components/tabs/table-tab.tsx";
import IdentityProviderGroupColumns from "@/models/ColumnDefinitions/IdentityProviderGroupColunms.ts";
import {getIdentityProviderGroupsByAccountId} from "@/services/mobileAccountService.ts";
import {appendFilter} from "@/utilities/filters.ts";
import SearchGroupModal from "@/components/map-groups/groupSearchModal.tsx";
import {useFormikContext} from "formik";
import identityProviderGroupSearch from "@/models/search/IdentityProviderGroupSearch.ts";

interface SelectGroupsProps {
    onNext: (e) => void,
    currentAccountId: number,
    showLegend?: boolean;
    title: string
    setSelectedGroup: (group: IdentityProviderGroup) => void;
    setStepDescriptions: React.Dispatch<React.SetStateAction<{ [key: string]: string }>>;
}

// This will search for a specific group the user wants,
// or a user can select an existing group from the list of non mapped groups.
// Once a group is selected, the user can click next to move to the next step of the mapping process which is selecting a profile for the group.
const SelectGroups: FC<SelectGroupsProps> = ({onNext, title, currentAccountId, setSelectedGroup, setStepDescriptions}) => {
    const {translate} = useLocale();
    const [isNextEnabled, setIsNextEnabled] = useState(false)
    const [openSearchView, setOpenSearchView] = useState(false);
    const {setFieldValue, values} = useFormikContext<any>();
    const initialMapping = {
        id: 0,
        idpGroupName: "",
        identityProviderGroupId: "",
        identityProviderGroupDesc: "",
        profileName: "",
        profileId: 0,
        accountName: "",
        accountId: 0,
    }

    const handleNext = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
        setStepDescriptions(prev => ({...prev, groupName: values.idpGroupName}))
        onNext(e);
    };

    const handleAdd = () => {
        setSelectedGroup(initialMapping)
        setOpenSearchView(true);
    }

    const getExistingGroupsByAccountId = (listParameters?: any): Promise<IdentityProviderGroupWrapper> => {
        return getIdentityProviderGroupsByAccountId(translate, {
            ...listParameters, filters: appendFilter(currentAccountId, 'Account', 'account.id', listParameters?.filters)
        });
    }

    const onRowClicked = async (rowData) => {
        rowData ? setIsNextEnabled(true) : setIsNextEnabled(false);
        await setFieldValue("idpGroupName", rowData?.idpGroupName);
        await setFieldValue("identityProviderGroupId", rowData?.identityProviderGroupId);
        await setFieldValue("idpGroupDescription", rowData?.idpGroupDescription);
        await setFieldValue("profileName", rowData?.profileName);
        await setFieldValue("profileId", rowData?.profileId);
        await setFieldValue("accountName", rowData?.accountName);
        await setFieldValue("accountId", rowData?.accountId);
        await setFieldValue("id", rowData?.id);
    }

    const onForceNextStep = (isEnabled: boolean) => {
        if (isEnabled) {
            onNext(isEnabled);
        }
    }

    return <div className="pt-3 pr-3 h-full relative">
        <div className='flex w-full' style={{maxHeight: '410px'}}>
            <TableContextProvider
                usePagination
                useTable
                useSort
                dataMethod={getExistingGroupsByAccountId}
                searchDescriptor={identityProviderGroupSearch}
                tableDescriptor={IdentityProviderGroupColumns}
                canExportReport={false}>
                <TableTab
                    title={title}
                    selectRows={false}
                    onRowClicked={onRowClicked}
                    cyName='search-groups-table'/>
            </TableContextProvider>
        </div>
        <div className="flex flex-row justify-between w-full absolute bottom-0 pr-3 pb-3">
            <button
                className="bg-lime text-white font-bold py-1 px-5 rounded-full "
                type="button"
                onClick={() => handleAdd()}>
                {translate.byKey(TranslationKeys.addGroup)}</button>
        </div>
        <div className="flex flex-row justify-end absolute bottom-0 right-0 pr-3 pb-3 ">
            <button
                className={`py-1 px-5 mt-3 secondary  ${isNextEnabled ? 'text-white bg-portal-blue-primary-shade2' : 'secondary-disabled cursor-not-allowed'}`}
                type="button"
                disabled={!isNextEnabled}
                onClick={handleNext}>{translate.byKey(TranslationKeys.next)}</button>
        </div>
        <SearchGroupModal
            isOpen={openSearchView}
            setIsOpen={setOpenSearchView}
            title={translate.byKey(TranslationKeys.searchGroup)}
            forceNextStep={onForceNextStep}
            setStepDescriptions={setStepDescriptions}/>
    </div>
}

export default SelectGroups;
